import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Tabs,
  Tab,
  Autocomplete,
  Card,
  CardActionArea,
  Divider,
  InputAdornment,
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RedeemIcon from '@mui/icons-material/Redeem';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  getCampaigns,
  createCampaign,
  updateCampaign,
  toggleCampaign,
  deleteCampaign,
  getCampaignUsages,
  simulateCampaignCart,
  CampaignUsageRow,
  CampaignSimulateResult,
  getEcommerceBrands,
  getEcommerceProductsByIds,
  getEcommerceProductsPaged,
  getPublicCategoriesWithHierarchy,
  CampaignData,
} from '../../../services/ecommerceApi';
import {
  CAMPAIGN_SPECIAL_DAYS,
  getSpecialDayLabel,
  suggestSpecialDayDates,
} from './campaignSpecialDays';
import {
  removeCampaignPromoCard,
  setCampaignPromoCardEnabled,
  syncCampaignPromoCard,
  consolidateCampaignPromoCards,
} from './campaignPromoCardSync';
import accountingService from '../../../services/accountingService';
import CampaignShippingPanel from './CampaignShippingPanel';
import GuideButton from '../../common/guide/GuideButton';

const CAMPAIGN_SECTION_KEY = 'gts.ecommerce.campaignSection';

interface CampaignManagementProps {
  companyId: string | null;
  configId?: string | null;
}

type CampaignType = 'ProductDiscount' | 'Coupon' | 'CartDiscount' | 'BuyXPayY' | 'FreeShipping';

const PRIMARY_TYPES: CampaignType[] = ['ProductDiscount', 'Coupon', 'CartDiscount', 'BuyXPayY', 'FreeShipping'];
const CHECKOUT_EXCLUSIVE_TYPES: CampaignType[] = ['Coupon', 'CartDiscount', 'BuyXPayY'];

interface OptionItem {
  id: string;
  name: string;
  stockCode?: string;
}

const PRODUCT_PAGE_SIZE = 30;

const mapCatalogItem = (item: any): OptionItem => ({
  id: String(item?.id ?? item?.Id ?? ''),
  name: String(item?.name || item?.Name || item?.stockCode || item?.StockCode || item?.id || item?.Id || ''),
  stockCode: String(item?.stockCode || item?.StockCode || ''),
});

const mergeOptions = (prev: OptionItem[], incoming: OptionItem[]): OptionItem[] => {
  const map = new Map<string, OptionItem>();
  prev.forEach((item) => { if (item.id) map.set(item.id, item); });
  incoming.forEach((item) => { if (item.id) map.set(item.id, item); });
  return Array.from(map.values());
};

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  active: 'success',
  upcoming: 'info',
  expired: 'default',
  passive: 'warning',
};

/** 6 haneli kupon: A–Z + 0–9 (karışıklık yaratan I,L,O,0,1 hariç). */
const generateRandomCouponCode = (length = 6): string => {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(length);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < length; i++) {
    out += alphabet[bytes[i] % alphabet.length];
  }
  return out;
};

const WIZARD_STEPS = [
  { key: 'type', labelTr: 'Tür', labelEn: 'Type', hintTr: 'Kampanyanın nasıl çalışacağını seçin', hintEn: 'Choose how the campaign works' },
  { key: 'info', labelTr: 'Bilgiler', labelEn: 'Details', hintTr: 'Ad, tarih ve görünürlük', hintEn: 'Name, dates and visibility' },
  { key: 'rules', labelTr: 'Kurallar', labelEn: 'Rules', hintTr: 'İndirim, kupon ve eşikler', hintEn: 'Discount, coupon and thresholds' },
  { key: 'scope', labelTr: 'Kapsam', labelEn: 'Scope', hintTr: 'Nerede geçerli ve son kontrol', hintEn: 'Where it applies and a final check' },
] as const;

const emptyForm = (): CampaignData => ({
  name: '',
  description: '',
  type: 'ProductDiscount',
  types: ['ProductDiscount'],
  startDate: '',
  endDate: '',
  discountType: 'Percentage',
  discountValue: 0,
  maxDiscountAmount: null,
  minCartAmount: null,
  buyQuantity: null,
  payQuantity: null,
  buyXPayYSameProductOnly: false,
  maxFreeUnits: null,
  freeUnitSelection: 'Cheapest',
  couponCode: '',
  usageLimit: null,
  usageLimitPerCustomer: null,
  allowStackWithOthers: false,
  scopeType: 'All',
  priority: 0,
  specialDay: '',
  isActive: true,
  productIds: [],
  categoryIds: [],
  brandIds: [],
});

const normalizeTypes = (c: any): CampaignType[] => {
  if (Array.isArray(c?.types) && c.types.length > 0) {
    return c.types.filter((t: string) =>
      ['ProductDiscount', 'Coupon', 'CartDiscount', 'BuyXPayY', 'FreeShipping'].includes(t)
    ) as CampaignType[];
  }
  if (c?.type) return [c.type as CampaignType];
  return ['ProductDiscount'];
};

const toLocalInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const listVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.03 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 8 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
  },
};

const CampaignManagement: React.FC<CampaignManagementProps> = ({ companyId, configId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [specialDayFilter, setSpecialDayFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [products, setProducts] = useState<OptionItem[]>([]);
  const [categories, setCategories] = useState<OptionItem[]>([]);
  const [brands, setBrands] = useState<OptionItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [productLoading, setProductLoading] = useState(false);
  const [productHasMore, setProductHasMore] = useState(true);
  const [productQuery, setProductQuery] = useState('');
  const productPageRef = useRef(1);
  const productHasMoreRef = useRef(true);
  const productLoadingRef = useRef(false);
  const productSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedProductIdsRef = useRef<string[]>([]);

  const [section, setSection] = useState<'campaigns' | 'shipping'>(() => {
    try {
      return sessionStorage.getItem(CAMPAIGN_SECTION_KEY) === 'shipping' ? 'shipping' : 'campaigns';
    } catch {
      return 'campaigns';
    }
  });
  const [editorOpen, setEditorOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CampaignData>(emptyForm());
  const [editSnapshot, setEditSnapshot] = useState<{
    types: CampaignType[];
    couponCode: string;
    discountType: string;
    buyQuantity: number | null;
    payQuantity: number | null;
    usedCount: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [usageTarget, setUsageTarget] = useState<any | null>(null);
  const [usageRows, setUsageRows] = useState<CampaignUsageRow[]>([]);
  const [usageTotal, setUsageTotal] = useState(0);
  const [usagePage, setUsagePage] = useState(0);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [simOpen, setSimOpen] = useState(false);
  const [simLines, setSimLines] = useState<Array<{ id: string; name: string; quantity: number }>>([]);
  const [simCoupon, setSimCoupon] = useState('');
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState<CampaignSimulateResult | null>(null);
  const [simError, setSimError] = useState<string | null>(null);

  const showToast = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const typeMeta = useMemo(
    () => ({
      ProductDiscount: { label: tt('Ürün İndirimi', 'Product Discount'), icon: <LocalOfferIcon />, desc: tt('Seçili ürün/kategori/markada yüzde veya tutar indirimi', 'Percentage or amount off selected products/categories/brands') },
      Coupon: { label: tt('Kupon Kodu', 'Coupon Code'), icon: <ConfirmationNumberIcon />, desc: tt('Sepette kod girerek indirim', 'Discount by entering a code at checkout') },
      CartDiscount: { label: tt('Sepet İndirimi', 'Cart Discount'), icon: <ShoppingCartIcon />, desc: tt('Sepet tutarı eşiğine göre indirim', 'Discount based on cart total threshold') },
      BuyXPayY: { label: tt('X Al Y Öde', 'Buy X Pay Y'), icon: <RedeemIcon />, desc: tt('Çok al az öde kampanyası', 'Buy more, pay less') },
      FreeShipping: { label: tt('Ücretsiz Kargo', 'Free Shipping'), icon: <LocalShippingIcon />, desc: tt('Eşik üzerinde ücretsiz kargo', 'Free shipping above a threshold') },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTr]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCampaigns(companyId || undefined);
      const data = res?.data || [];
      setCampaigns(Array.isArray(data) ? data : []);
      if (companyId) {
        try {
          await consolidateCampaignPromoCards(companyId, configId, isTr);
        } catch (cardErr) {
          console.warn('[CampaignManagement] Kampanya kartları birleştirilemedi:', cardErr);
        }
      }
    } catch (err) {
      console.error('[CampaignManagement] Kampanyalar yüklenemedi:', err);
      showToast(tt('Kampanyalar yüklenemedi.', 'Failed to load campaigns.'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId) load();
  }, [companyId, load]);

  useEffect(() => {
    try {
      sessionStorage.removeItem(CAMPAIGN_SECTION_KEY);
    } catch {
      // storage blocked
    }
  }, []);

  const loadProductPage = useCallback(async (page: number, search: string, reset: boolean) => {
    if (!companyId || productLoadingRef.current) return;
    if (!reset && !productHasMoreRef.current) return;
    productLoadingRef.current = true;
    setProductLoading(true);
    try {
      const res = await getEcommerceProductsPaged({
        page,
        pageSize: PRODUCT_PAGE_SIZE,
        search,
      });
      const mapped = res.items.map(mapCatalogItem).filter((item) => item.id);
      setProducts((prev) => {
        const keepSelected = prev.filter((item) => selectedProductIdsRef.current.includes(item.id));
        return mergeOptions(reset ? keepSelected : prev, mapped);
      });
      productPageRef.current = page;
      productHasMoreRef.current = res.hasMore;
      setProductHasMore(res.hasMore);
    } catch (err) {
      console.error('[CampaignManagement] Ürünler yüklenemedi:', err);
    } finally {
      productLoadingRef.current = false;
      setProductLoading(false);
    }
  }, [companyId]);

  const resolveSelectedProducts = useCallback(async (ids: string[]) => {
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return;
    try {
      const rows = await getEcommerceProductsByIds(unique);
      setProducts((prev) => mergeOptions(prev, rows.map(mapCatalogItem)));
    } catch (err) {
      console.error('[CampaignManagement] Seçili ürün adları yüklenemedi:', err);
      setProducts((prev) => mergeOptions(prev, unique.map((id) => ({ id, name: id }))));
    }
  }, []);

  const mapCategoryOption = (c: any): OptionItem => {
    const name = String(c?.name || c?.Name || c?.id || c?.Id || '');
    const level = Number(c?.level || c?.Level || 1);
    const prefix = level > 1 ? `${'— '.repeat(level - 1)}` : '';
    return { id: String(c?.id || c?.Id || ''), name: `${prefix}${name}` };
  };

  // Kategori: önce muhasebe (canlı, çalışıyor). E-ticaret API cevap verirse onu ezer.
  const ensureCatalog = useCallback(async (selectedIds: string[] = [], force = false) => {
    if (!companyId) return;
    const needCategories = force || categories.length === 0;
    const needBrands = force || brands.length === 0;
    if (!needCategories && !needBrands && selectedIds.length === 0) return;
    setCatalogError(null);
    if (needCategories || needBrands) setCatalogLoading(true);
    try {
      if (needCategories) {
        let mapped: OptionItem[] = [];
        try {
          const inventory = await accountingService.getCategories(false);
          const list = Array.isArray(inventory) ? inventory : [];
          const ecommerceOnly = list.filter((c: any) => c.IsEcommerceEnabled ?? c.isEcommerceEnabled);
          const source = ecommerceOnly.length > 0 ? ecommerceOnly : list;
          mapped = source.map(mapCategoryOption).filter((c) => c.id);
          if (mapped.length > 0) setCategories(mapped);
        } catch (err) {
          console.warn('[CampaignManagement] Muhasebe kategorileri alınamadı:', err);
        }

        void getPublicCategoriesWithHierarchy(companyId).then((cats) => {
          const fromEcom = (Array.isArray(cats) ? cats : []).map(mapCategoryOption).filter((c) => c.id);
          if (fromEcom.length > 0) setCategories(fromEcom);
        });

        if (mapped.length === 0) {
          setCatalogError(tt('Kategoriler yüklenemedi. Yeniden deneyin.', 'Categories could not be loaded. Please retry.'));
        }
      }
      if (needBrands) {
        try {
          const list = await getEcommerceBrands(companyId);
          setBrands(
            (Array.isArray(list) ? list : []).map((b: any) => ({
              id: String(b.id || b.Id || ''),
              name: String(b.name || b.Name || b.id || ''),
            })).filter((b) => b.id)
          );
        } catch (err) {
          console.warn('[CampaignManagement] Markalar yüklenemedi:', err);
        }
      }
      if (selectedIds.length > 0) {
        await resolveSelectedProducts(selectedIds);
      }
    } catch (err) {
      console.error('[CampaignManagement] Katalog yüklenemedi:', err);
      setCatalogError(tt('Katalog yüklenemedi. API yanıt vermiyor.', 'Catalog could not be loaded. API is not responding.'));
    } finally {
      setCatalogLoading(false);
    }
  }, [companyId, categories.length, brands.length, resolveSelectedProducts, tt]);

  const closeEditor = () => {
    if (saving) return;
    setEditorOpen(false);
    setWizardStep(0);
  };

  const resetProductPaging = () => {
    productPageRef.current = 1;
    productHasMoreRef.current = true;
    setProductHasMore(true);
    setProductQuery('');
  };

  const nextWeekRange = () => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 7);
    return { startDate: toLocalInput(start.toISOString()), endDate: toLocalInput(end.toISOString()) };
  };

  const openRepeat = async (c: any) => {
    const types = normalizeTypes(c);
    const productIds = c.productIds || [];
    const dates = nextWeekRange();
    setEditingId(null);
    setEditSnapshot(null);
    selectedProductIdsRef.current = productIds;
    setForm({
      ...emptyForm(),
      ...c,
      id: undefined,
      types,
      type: types[0] || 'ProductDiscount',
      name: `${c.name || tt('Kampanya', 'Campaign')}`.replace(/\s+\(\d+\)$/, ''),
      startDate: dates.startDate,
      endDate: dates.endDate,
      couponCode: types.includes('Coupon') ? generateRandomCouponCode(6) : '',
      usedCount: 0,
      isActive: true,
      priority: 0,
      productIds,
      categoryIds: c.categoryIds || [],
      brandIds: c.brandIds || [],
    });
    resetProductPaging();
    setWizardStep(1);
    setEditorOpen(true);
    await ensureCatalog(productIds);
  };

  const openCreate = async () => {
    setEditingId(null);
    setEditSnapshot(null);
    setForm(emptyForm());
    selectedProductIdsRef.current = [];
    resetProductPaging();
    setWizardStep(0);
    setEditorOpen(true);
    await ensureCatalog();
  };

  const openEdit = async (c: any) => {
    setEditingId(c.id);
    const types = normalizeTypes(c);
    const productIds = c.productIds || [];
    selectedProductIdsRef.current = productIds;
    setEditSnapshot({
      types,
      couponCode: String(c.couponCode || ''),
      discountType: String(c.discountType || 'Percentage'),
      buyQuantity: c.buyQuantity != null ? Number(c.buyQuantity) : null,
      payQuantity: c.payQuantity != null ? Number(c.payQuantity) : null,
      usedCount: Number(c.usedCount) || 0,
    });
    setForm({
      ...emptyForm(),
      ...c,
      types,
      type: types[0] || 'ProductDiscount',
      startDate: toLocalInput(c.startDate),
      endDate: toLocalInput(c.endDate),
      productIds,
      categoryIds: c.categoryIds || [],
      brandIds: c.brandIds || [],
    });
    resetProductPaging();
    setWizardStep(0);
    setEditorOpen(true);
    await ensureCatalog(productIds);
  };

  const setField = <K extends keyof CampaignData>(key: K, value: CampaignData[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    selectedProductIdsRef.current = form.productIds || [];
  }, [form.productIds]);

  useEffect(() => {
    if (!editorOpen || !companyId) return;
    if (!editingId && wizardStep !== 3) return;
    loadProductPage(1, productQuery, true);
    void ensureCatalog(form.categoryIds || [], categories.length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, wizardStep, companyId, editingId]);

  const selectedTypes = (form.types && form.types.length > 0 ? form.types : [form.type]) as CampaignType[];
  const hasType = (key: CampaignType) => selectedTypes.includes(key);
  const primaryType: CampaignType =
    selectedTypes.find((t) => t !== 'FreeShipping')
    || (hasType('FreeShipping') ? 'FreeShipping' : 'ProductDiscount');
  const hasLegacyTypeMix = CHECKOUT_EXCLUSIVE_TYPES.filter((t) => hasType(t)).length > 1;

  const selectPrimary = (key: CampaignType) => {
    setForm((prev) => {
      const current = (prev.types && prev.types.length > 0 ? prev.types : [prev.type]) as CampaignType[];
      const keepShipping = key !== 'FreeShipping' && current.includes('FreeShipping');
      const next: CampaignType[] = keepShipping ? [key, 'FreeShipping'] : [key];
      return {
        ...prev,
        types: next,
        type: key,
        ...(key === 'Coupon' && !prev.couponCode?.trim()
          ? { couponCode: generateRandomCouponCode(6) }
          : {}),
      };
    });
  };

  const toggleShippingAddon = () => {
    setForm((prev) => {
      const current = (prev.types && prev.types.length > 0 ? prev.types : [prev.type]) as CampaignType[];
      const primary = current.find((t) => t !== 'FreeShipping') || 'ProductDiscount';
      if (!current.find((t) => t !== 'FreeShipping')) return prev;
      const hasShip = current.includes('FreeShipping');
      const next: CampaignType[] = hasShip
        ? current.filter((t) => t !== 'FreeShipping')
        : [...current.filter((t) => t !== 'FreeShipping'), 'FreeShipping'];
      return {
        ...prev,
        types: next,
        type: primary,
      };
    });
  };

  const handleSpecialDayChange = (key: string) => {
    const suggested = suggestSpecialDayDates(key);
    setForm((prev) => ({
      ...prev,
      specialDay: key || '',
      ...(suggested
        ? { startDate: suggested.startDate, endDate: suggested.endDate }
        : {}),
    }));
  };

  const validateStep = (step: number): string | null => {
    if (step === 0) {
      if (selectedTypes.length === 0) return tt('En az bir kampanya türü seçin.', 'Select at least one campaign type.');
    }
    if (step === 1) {
      if (!form.name.trim()) return tt('Kampanya adı zorunludur.', 'Campaign name is required.');
      if (!form.startDate || !form.endDate) return tt('Başlangıç ve bitiş tarihi zorunludur.', 'Start and end dates are required.');
      if (new Date(form.endDate) < new Date(form.startDate)) return tt('Bitiş tarihi başlangıçtan önce olamaz.', 'End date cannot be before start date.');
    }
    if (step === 2) {
      if (hasType('Coupon') && form.couponCode?.trim()) {
        const code = form.couponCode.trim().toUpperCase();
        if (!/^[A-Z0-9]+$/.test(code)) {
          return tt('Kupon kodu yalnızca harf ve rakam olabilir.', 'Coupon code may only contain letters and digits.');
        }
        if (!editingId && code.length !== 6) {
          return tt('Kupon kodu 6 haneli olmalıdır.', 'Coupon code must be 6 characters.');
        }
      }
      if (hasType('BuyXPayY')) {
        const b = Number(form.buyQuantity), p = Number(form.payQuantity);
        if (!b || !p || p >= b) return tt("'Al' adedi 'öde' adedinden büyük olmalıdır.", "'Buy' quantity must exceed 'pay' quantity.");
      }
    }
    if (step === 3) {
      if (form.scopeType === 'Products' && !(form.productIds || []).length) {
        return tt('En az bir ürün seçin.', 'Select at least one product.');
      }
      if (form.scopeType === 'Categories' && !(form.categoryIds || []).length) {
        return tt('En az bir kategori seçin.', 'Select at least one category.');
      }
      if (form.scopeType === 'Brands' && !(form.brandIds || []).length) {
        return tt('En az bir marka seçin.', 'Select at least one brand.');
      }
    }
    return null;
  };

  const goToStep = (target: number) => {
    if (target === wizardStep || target < 0 || target >= WIZARD_STEPS.length) return;
    if (target < wizardStep) {
      setWizardStep(target);
      return;
    }
    for (let i = wizardStep; i < target; i++) {
      const err = validateStep(i);
      if (err) {
        showToast(err, 'error');
        setWizardStep(i);
        return;
      }
    }
    setWizardStep(target);
  };

  const goNext = () => goToStep(wizardStep + 1);
  const goBack = () => setWizardStep((s) => Math.max(0, s - 1));

  const scrollToSection = (step: number) => {
    const ids = ['campaign-section-type', 'campaign-section-info', 'campaign-section-rules', 'campaign-section-scope'];
    document.getElementById(ids[step])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSave = async () => {
    for (let i = 0; i < WIZARD_STEPS.length; i++) {
      const err = validateStep(i);
      if (err) {
        showToast(err, 'error');
        if (editingId) scrollToSection(i);
        else setWizardStep(i);
        return;
      }
    }

    setSaving(true);
    try {
      const types = (() => {
        if (editingId && editSnapshot && !hasLegacyTypeMix) {
          return [...editSnapshot.types];
        }
        const unique = selectedTypes.length > 0 ? [...selectedTypes] : ['ProductDiscount' as CampaignType];
        const shipping = unique.includes('FreeShipping');
        const primaries = unique.filter((t) => t !== 'FreeShipping');
        const exclusive = primaries.filter((t) => CHECKOUT_EXCLUSIVE_TYPES.includes(t));
        const primary: CampaignType = primaries.includes('ProductDiscount')
          ? 'ProductDiscount'
          : exclusive[0] || primaries[0] || (shipping ? 'FreeShipping' : 'ProductDiscount');
        if (primary === 'FreeShipping') return ['FreeShipping'] as CampaignType[];
        return (shipping ? [primary, 'FreeShipping'] : [primary]) as CampaignType[];
      })();
      const couponCode = (() => {
        if (editingId && editSnapshot && editSnapshot.usedCount > 0 && types.includes('Coupon')) {
          return editSnapshot.couponCode;
        }
        return types.includes('Coupon')
          ? (form.couponCode?.trim().toUpperCase() || generateRandomCouponCode(6))
          : (form.couponCode || '');
      })();
      if (types.includes('Coupon')) {
        setField('couponCode', couponCode);
      }
      const payload: CampaignData = {
        ...form,
        companyId: companyId || undefined,
        types,
        type: types.includes('ProductDiscount') ? 'ProductDiscount' : types[0],
        couponCode,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        discountValue: Number(form.discountValue) || 0,
        maxDiscountAmount: form.maxDiscountAmount != null && form.maxDiscountAmount !== ('' as any) ? Number(form.maxDiscountAmount) : null,
        minCartAmount: form.minCartAmount != null && form.minCartAmount !== ('' as any) ? Number(form.minCartAmount) : null,
        discountType: editingId && editSnapshot && editSnapshot.usedCount > 0
          ? editSnapshot.discountType
          : form.discountType,
        buyQuantity: editingId && editSnapshot && editSnapshot.usedCount > 0
          ? editSnapshot.buyQuantity
          : (form.buyQuantity ? Number(form.buyQuantity) : null),
        payQuantity: editingId && editSnapshot && editSnapshot.usedCount > 0
          ? editSnapshot.payQuantity
          : (form.payQuantity ? Number(form.payQuantity) : null),
        buyXPayYSameProductOnly: types.includes('BuyXPayY') ? !!form.buyXPayYSameProductOnly : false,
        maxFreeUnits: types.includes('BuyXPayY') && form.maxFreeUnits ? Number(form.maxFreeUnits) : null,
        freeUnitSelection: types.includes('BuyXPayY')
          ? (form.freeUnitSelection === 'Expensive' ? 'Expensive' : 'Cheapest')
          : null,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
        usageLimitPerCustomer: form.usageLimitPerCustomer ? Number(form.usageLimitPerCustomer) : null,
        allowStackWithOthers: types.includes('Coupon') ? !!form.allowStackWithOthers : false,
        priority: 0,
        specialDay: form.specialDay?.trim() || null,
      };

      let savedId = editingId;
      if (editingId) {
        await updateCampaign(editingId, payload);
      } else {
        const created = await createCampaign(payload);
        savedId = created?.data?.id || created?.id || null;
      }

      if (companyId && savedId) {
        try {
          await syncCampaignPromoCard({
            companyId,
            configId,
            campaignId: savedId,
            campaign: { ...payload, id: savedId },
            isTr,
          });
        } catch (cardErr) {
          console.error('[CampaignManagement] Promosyon kartı senkronize edilemedi:', cardErr);
        }
      }

      showToast(
        editingId
          ? tt('Kampanya güncellendi. Promosyon kartı senkronize edildi.', 'Campaign updated. Promo card synced.')
          : tt('Kampanya oluşturuldu. Kart Oluştur sekmesinde otomatik kart tanımlandı.', 'Campaign created. A promo card was added under Create Card.')
      );
      setEditorOpen(false);
      setWizardStep(0);
      await load();
    } catch (err: any) {
      const msg = err?.response?.data?.message || tt('Kampanya kaydedilemedi.', 'Failed to save campaign.');
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (c: any) => {
    try {
      const nextActive = !c.isActive;
      await toggleCampaign(c.id, nextActive, companyId || undefined);
      if (companyId) {
        try {
          if (nextActive) {
            await syncCampaignPromoCard({
              companyId,
              configId,
              campaignId: c.id,
              campaign: c,
              isTr,
            });
          } else {
            await setCampaignPromoCardEnabled(companyId, c.id, false);
          }
        } catch (cardErr) {
          console.error('[CampaignManagement] Promosyon kartı durumu güncellenemedi:', cardErr);
        }
      }
      await load();
    } catch (err) {
      showToast(tt('Durum güncellenemedi.', 'Failed to update status.'), 'error');
    }
  };

  const loadUsages = async (campaign: any, pageIndex = 0) => {
    if (!campaign?.id) return;
    setUsageLoading(true);
    setUsageError(null);
    try {
      const res = await getCampaignUsages(campaign.id, companyId || undefined, pageIndex + 1, 25);
      setUsageRows(Array.isArray(res?.data) ? res.data : []);
      setUsageTotal(Number(res?.total) || 0);
      setUsagePage(pageIndex);
      const liveCount = Number(res?.usedCount ?? res?.total);
      if (Number.isFinite(liveCount)) {
        setCampaigns((prev) => prev.map((c) => (c.id === campaign.id ? { ...c, usedCount: liveCount } : c)));
        setUsageTarget((prev) => (prev && prev.id === campaign.id ? { ...prev, usedCount: liveCount } : prev));
      }
      try {
        const list = await getCampaigns(companyId || undefined);
        const data = list?.data || [];
        if (Array.isArray(data)) setCampaigns(data);
      } catch {
        // tablo sayısı diyalogdan güncellendi; liste yenilenemezse sessiz geç
      }
    } catch (err: any) {
      console.error('[CampaignManagement] Kullanımlar yüklenemedi:', err);
      setUsageRows([]);
      setUsageTotal(0);
      const status = err?.response?.status;
      const reqUrl = `${err?.config?.baseURL || ''}${err?.config?.url || ''}`;
      const remote = /gtsecommerce\.aquasoft\.com\.tr/i.test(reqUrl);
      setUsageError(
        remote
          ? tt(
              'Panel canlı e-ticaret API’sine gidiyor; bu yüzden kullanım listesi yok. gts-web’i (npm start) durdurup yeniden başlatın — .env.local localhost:5003 kullanacak.',
              'The panel is calling the live ecommerce API, so this list is missing. Restart gts-web (npm start) so .env.local uses localhost:5003.'
            )
          : status
            ? tt(`Kullanım listesi yüklenemedi (${status}).`, `Usage list could not be loaded (${status}).`)
            : tt('Kullanım listesi yüklenemedi.', 'Usage list could not be loaded.')
      );
      showToast(tt('Kullanım listesi yüklenemedi.', 'Usage list could not be loaded.'), 'error');
    } finally {
      setUsageLoading(false);
    }
  };

  const openSimulator = () => {
    setSimOpen(true);
    setSimResult(null);
    setSimError(null);
    if (products.length === 0) void loadProductPage(1, '', true);
  };

  const runSimulator = async () => {
    if (simLines.length === 0) {
      setSimError(tt('En az bir ürün ekleyin.', 'Add at least one product.'));
      return;
    }
    setSimLoading(true);
    setSimError(null);
    try {
      const res = await simulateCampaignCart(
        simLines.map((l) => ({ productId: l.id, quantity: l.quantity })),
        { couponCode: simCoupon.trim() || undefined, companyId: companyId || undefined }
      );
      if (!res?.success || !res.data) {
        setSimResult(null);
        setSimError(res?.message || tt('Simülasyon çalışmadı.', 'Simulation failed.'));
        return;
      }
      setSimResult(res.data);
    } catch (err: any) {
      setSimResult(null);
      setSimError(err?.response?.data?.message || tt('Simülasyon çalışmadı.', 'Simulation failed.'));
    } finally {
      setSimLoading(false);
    }
  };

  const openUsages = (campaign: any) => {
    setUsageTarget(campaign);
    setUsageRows([]);
    setUsageTotal(0);
    setUsagePage(0);
    setUsageError(null);
    void loadUsages(campaign, 0);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCampaign(deleteTarget.id, companyId || undefined);
      if (companyId) {
        try {
          await removeCampaignPromoCard(companyId, deleteTarget.id);
        } catch (cardErr) {
          console.error('[CampaignManagement] Promosyon kartı silinemedi:', cardErr);
        }
      }
      showToast(tt('Kampanya silindi.', 'Campaign deleted.'));
      setDeleteTarget(null);
      await load();
    } catch (err) {
      showToast(tt('Kampanya silinemedi.', 'Failed to delete campaign.'), 'error');
    }
  };

  const statusLabel = (status: string) =>
    ({
      active: tt('Aktif', 'Active'),
      upcoming: tt('Yaklaşan', 'Upcoming'),
      expired: tt('Süresi Doldu', 'Expired'),
      passive: tt('Pasif', 'Passive'),
    } as Record<string, string>)[status] || status;

  const typeLabel = (type: string) => (typeMeta as any)[type]?.label || type;
  const typesLabel = (c: any) => {
    const types = normalizeTypes(c);
    return types.map((t) => typeLabel(t)).join(' + ');
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(localeCode, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    return new Date(value).toLocaleString(localeCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMoney = (amount?: number) =>
    `${Number(amount || 0).toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${tt('TL', 'TRY')}`;

  const discountLabel = (c: any) => {
    if (c.type === 'FreeShipping') return tt('Ücretsiz Kargo', 'Free Shipping');
    if (c.type === 'BuyXPayY') return `${c.buyQuantity} → ${c.payQuantity}`;
    if (c.discountType === 'Percentage') return `%${c.discountValue}`;
    return `${c.discountValue} ${tt('TL', 'TRY')}`;
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      const day = String(c.specialDay || '');
      if (specialDayFilter === 'none' && day) return false;
      if (specialDayFilter !== 'all' && specialDayFilter !== 'none' && day !== specialDayFilter) return false;
      if (
        term &&
        !(`${c.name} ${c.couponCode || ''} ${getSpecialDayLabel(c.specialDay, isTr)}`.toLowerCase().includes(term))
      ) {
        return false;
      }
      return true;
    });
  }, [campaigns, statusFilter, specialDayFilter, searchTerm, isTr]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: campaigns.length, active: 0, upcoming: 0, expired: 0, passive: 0 };
    campaigns.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
    return counts;
  }, [campaigns]);

  const showDiscountValue = hasType('ProductDiscount') || hasType('Coupon') || hasType('CartDiscount');
  const showMinCart = hasType('Coupon') || hasType('CartDiscount') || hasType('FreeShipping');
  const competingCheckoutTypes = selectedTypes.filter(
    (t) => t === 'Coupon' || t === 'CartDiscount' || t === 'BuyXPayY'
  );
  const sharedDiscountTypeLabels = selectedTypes
    .filter((t) => t === 'ProductDiscount' || t === 'Coupon' || t === 'CartDiscount')
    .map((t) => typeLabel(t));

  const productLabel = (item: OptionItem) =>
    item.stockCode ? `${item.name} (${item.stockCode})` : item.name;

  const scopeLabel = () => {
    if (form.scopeType === 'Products') {
      const n = (form.productIds || []).length;
      return n ? tt(`${n} ürün`, `${n} products`) : tt('Seçili ürünler', 'Selected products');
    }
    if (form.scopeType === 'Categories') {
      const n = (form.categoryIds || []).length;
      return n ? tt(`${n} kategori`, `${n} categories`) : tt('Seçili kategoriler', 'Selected categories');
    }
    if (form.scopeType === 'Brands') {
      const n = (form.brandIds || []).length;
      return n ? tt(`${n} marka`, `${n} brands`) : tt('Seçili markalar', 'Selected brands');
    }
    return tt('Tüm ürünler', 'All products');
  };

  const rulesSummary = () => {
    const parts: string[] = [];
    if (showDiscountValue) {
      parts.push(form.discountType === 'Percentage' ? `%${form.discountValue}` : `${form.discountValue} ${tt('TL', 'TRY')}`);
      if (form.discountType === 'Percentage' && form.maxDiscountAmount) {
        parts.push(tt(`azami ${form.maxDiscountAmount} TL`, `max ${form.maxDiscountAmount} TRY`));
      }
    }
    if (hasType('BuyXPayY') && form.buyQuantity && form.payQuantity) {
      parts.push(`${form.buyQuantity} → ${form.payQuantity}`);
    }
    if (hasType('FreeShipping')) parts.push(tt('Ücretsiz kargo', 'Free shipping'));
    if (showMinCart && form.minCartAmount) parts.push(tt(`min. sepet ${form.minCartAmount} TL`, `min. cart ${form.minCartAmount} TRY`));
    if (hasType('Coupon') && form.couponCode) parts.push(form.couponCode);
    return parts.join(' · ') || '—';
  };

  const isEditing = Boolean(editingId);
  const usedCount = Number(editSnapshot?.usedCount ?? form.usedCount) || 0;
  const hasUsages = isEditing && usedCount > 0;
  const lockMechanic = isEditing && !hasLegacyTypeMix;
  const currentStep = WIZARD_STEPS[wizardStep];

  return (
    <Box>
      {!editorOpen && (
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={section}
            onChange={(_, value: 'campaigns' | 'shipping') => setSection(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              value="campaigns"
              icon={<CampaignIcon />}
              iconPosition="start"
              label={tt('Kampanyalar', 'Campaigns')}
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
            <Tab
              value="shipping"
              icon={<LocalShippingIcon />}
              iconPosition="start"
              label={tt('Kargo', 'Shipping')}
              sx={{ textTransform: 'none', minHeight: 48 }}
            />
          </Tabs>
        </Paper>
      )}
      {section === 'shipping' && !editorOpen ? (
        <CampaignShippingPanel companyId={companyId} configId={configId} />
      ) : editorOpen ? (
        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton size="small" onClick={closeEditor} disabled={saving} aria-label={tt('Listeye dön', 'Back to list')}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" fontWeight={700} noWrap>
                {isEditing ? tt('Kampanyayı Düzenle', 'Edit Campaign') : tt('Yeni Kampanya', 'New Campaign')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isEditing
                  ? tt('Ad, tarih, tutar ve kapsam açık. Tür ve kullanılan kupon kilitli kalır.', 'Name, dates, value and scope stay editable. Type and a used coupon stay locked.')
                  : (isTr ? currentStep.hintTr : currentStep.hintEn)}
              </Typography>
            </Box>
            {!isEditing && (
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {wizardStep + 1} / {WIZARD_STEPS.length}
              </Typography>
            )}
            <GuideButton guideKey="ecommerce.campaigns" size="small" sx={{ flexShrink: 0 }} />
          </Box>

          {!isEditing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              mb: 2.5,
              overflowX: 'auto',
            }}
          >
            {WIZARD_STEPS.map((step, idx) => {
              const done = idx < wizardStep;
              const active = idx === wizardStep;
              return (
                <Box key={step.key} sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => goToStep(idx)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      border: 0,
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      p: 0,
                      minWidth: 0,
                      color: active ? 'primary.main' : done ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: active || done ? 'primary.main' : 'action.hover',
                        color: active || done ? 'primary.contrastText' : 'text.secondary',
                        transition: 'background-color 120ms ease, color 120ms ease',
                      }}
                    >
                      {done ? <CheckIcon sx={{ fontSize: 16 }} /> : idx + 1}
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight={active ? 700 : 500}
                      sx={{ display: { xs: idx === wizardStep ? 'block' : 'none', sm: 'block' } }}
                    >
                      {isTr ? step.labelTr : step.labelEn}
                    </Typography>
                  </Box>
                  {idx < WIZARD_STEPS.length - 1 && (
                    <Box
                      sx={{
                        flex: 1,
                        height: 2,
                        mx: 1,
                        bgcolor: done ? 'primary.main' : 'divider',
                        transition: 'background-color 120ms ease',
                        minWidth: 12,
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
          )}

          <Box
            key={isEditing ? 'edit-all' : wizardStep}
            sx={{
              minHeight: isEditing ? 0 : 280,
              ...(!isEditing
                ? {
                    animation: 'campaignStepIn 140ms ease-out',
                    '@keyframes campaignStepIn': {
                      from: { opacity: 0, transform: 'translateX(10px)' },
                      to: { opacity: 1, transform: 'translateX(0)' },
                    },
                  }
                : {}),
            }}
          >
            {(isEditing || wizardStep === 0) && (
              <Box id="campaign-section-type" sx={isEditing ? { mb: 3 } : undefined}>
                {lockMechanic ? (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>{tt('Tür', 'Type')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {tt('Kampanyanın çalışma şekli. Kayıt sonrası değişmez.', 'How the campaign works. Locked after creation.')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                      {selectedTypes.map((key) => (
                        <Chip
                          key={key}
                          icon={React.cloneElement((typeMeta as any)[key].icon, { fontSize: 'small' })}
                          label={(typeMeta as any)[key].label}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                      <Chip
                        size="small"
                        icon={<LockOutlinedIcon />}
                        label={tt('Kilitli', 'Locked')}
                        sx={{ height: 24 }}
                      />
                    </Box>
                    <Alert severity="info">
                      {tt(
                        'Shopify / Trendyol tarzı: tür oluşturulunca kilitlenir. Yanlış türse bu kaydı kapatıp yeni kampanya açın.',
                        'Shopify / Trendyol style: type is locked after create. If the type is wrong, close this record and create a new campaign.'
                      )}
                    </Alert>
                  </Box>
                ) : (
                  <>
                {isEditing && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{tt('Tür', 'Type')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tt('Kampanyanın nasıl çalışacağını seçin', 'Choose how the campaign works')}
                    </Typography>
                  </Box>
                )}
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{tt('Kampanya nasıl çalışsın?', 'How should this campaign work?')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {tt(
                    'Bir ana tür seç. İki ayrı kampanya (ör. ürün indirimi + kupon) sepette birleşebilir; kuponu müşteri ekler. Otomatik indirimler arasında daha avantajlı olan uygulanır. Ücretsiz kargo yanına eklenebilir.',
                    'Pick one main type. Two campaigns (for example product discount + coupon) can combine; the customer adds the coupon. Automatic discounts pick the better saving. Free shipping can sit beside another type.'
                  )}
                </Typography>
                {hasLegacyTypeMix && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {tt(
                      'Bu kayıtta birden fazla sepet indirimi türü var. Bir ana tür seç; kaydedince paket sadeleşir. Diğerini ayrı kampanya olarak aç.',
                      'This record has more than one checkout discount type. Pick one main type to simplify the package. Open the other as its own campaign.'
                    )}
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.5,
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
                  }}
                >
                  {PRIMARY_TYPES.map((key) => {
                    const meta = (typeMeta as any)[key];
                    const selected = primaryType === key;
                    return (
                      <Card
                        key={key}
                        variant="outlined"
                        sx={{
                          height: '100%',
                          borderColor: selected ? 'primary.main' : 'divider',
                          borderWidth: selected ? 2 : 1,
                          bgcolor: selected ? 'action.selected' : 'background.paper',
                          transition: 'border-color 120ms ease, background-color 120ms ease',
                        }}
                      >
                          <CardActionArea
                            onClick={() => selectPrimary(key)}
                            sx={{
                              p: 2,
                              height: '100%',
                              display: 'flex',
                              flexDirection: { xs: 'row', lg: 'column' },
                              alignItems: { xs: 'flex-start', lg: 'center' },
                              textAlign: { lg: 'center' },
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                bgcolor: selected ? 'primary.main' : 'action.hover',
                                color: selected ? 'primary.contrastText' : 'text.secondary',
                              }}
                            >
                              {meta.icon}
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { lg: 'center' }, gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
                                <Typography variant="body2" fontWeight={700}>{meta.label}</Typography>
                                {selected && (
                                  <Chip size="small" color="primary" label={tt('Ana tür', 'Main type')} sx={{ height: 18, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                                {meta.desc}
                              </Typography>
                            </Box>
                          </CardActionArea>
                        </Card>
                    );
                  })}
                </Box>

                {primaryType !== 'FreeShipping' && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{tt('Yanına eklenebilir', 'Can add beside it')}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {tt(
                        'Yalnızca ücretsiz kargo aynı pakette durabilir. Kupon / sepet indirimi / X al Y öde buraya gelmez.',
                        'Only free shipping can sit in the same package. Coupon / cart discount / Buy X Pay Y stay out.'
                      )}
                    </Typography>
                    <Box sx={{ maxWidth: { sm: 360 } }}>
                      {(() => {
                        const meta = typeMeta.FreeShipping;
                        const selected = hasType('FreeShipping');
                        return (
                          <Card
                            variant="outlined"
                            sx={{
                              borderColor: selected ? 'primary.main' : 'divider',
                              borderWidth: selected ? 2 : 1,
                              bgcolor: selected ? 'action.selected' : 'background.paper',
                            }}
                          >
                            <CardActionArea
                              onClick={toggleShippingAddon}
                              sx={{ p: 2, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1.5,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  bgcolor: selected ? 'primary.main' : 'action.hover',
                                  color: selected ? 'primary.contrastText' : 'text.secondary',
                                }}
                              >
                                {meta.icon}
                              </Box>
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                  <Typography variant="body2" fontWeight={700}>{meta.label}</Typography>
                                  <Chip
                                    size="small"
                                    color={selected ? 'primary' : 'default'}
                                    label={selected ? tt('Eklendi', 'Added') : tt('Opsiyonel', 'Optional')}
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                                  {tt(
                                    'Eşik dolunca kargo ücretsiz olur. İndirimle yarışmaz.',
                                    'Shipping becomes free at the threshold. Does not compete with the discount.'
                                  )}
                                </Typography>
                              </Box>
                            </CardActionArea>
                          </Card>
                        );
                      })()}
                    </Box>
                  </Box>
                )}
                  </>
                )}
              </Box>
            )}

            {(isEditing || wizardStep === 1) && (
              <Box id="campaign-section-info" sx={isEditing ? { mb: 3, pt: 1, borderTop: 1, borderColor: 'divider' } : undefined}>
                {isEditing && (
                  <Box sx={{ mb: 2, mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{tt('Bilgiler', 'Details')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tt('Ad, tarih ve görünürlük', 'Name, dates and visibility')}
                    </Typography>
                  </Box>
                )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={tt('Kampanya Adı', 'Campaign Name')}
                    fullWidth size="small" required
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    autoFocus={!isEditing}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={tt('Açıklama', 'Description')}
                    fullWidth size="small" multiline minRows={2}
                    value={form.description || ''}
                    onChange={(e) => setField('description', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{tt('Özel Gün / Sezon', 'Special Day / Season')}</InputLabel>
                    <Select
                      label={tt('Özel Gün / Sezon', 'Special Day / Season')}
                      value={form.specialDay || ''}
                      onChange={(e) => handleSpecialDayChange(String(e.target.value))}
                    >
                      {CAMPAIGN_SPECIAL_DAYS.map((day) => (
                        <MenuItem key={day.key || 'none'} value={day.key}>
                          {isTr ? day.labelTr : day.labelEn}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {tt(
                      'Seçince önerilen tarih aralığı otomatik doldurulur; isterseniz değiştirebilirsiniz.',
                      'Selecting a day auto-fills suggested dates; you can edit them anytime.'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label={tt('Başlangıç', 'Start')}
                    type="datetime-local" fullWidth size="small" required
                    InputLabelProps={{ shrink: true }}
                    value={form.startDate}
                    onChange={(e) => setField('startDate', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label={tt('Bitiş', 'End')}
                    type="datetime-local" fullWidth size="small" required
                    InputLabelProps={{ shrink: true }}
                    value={form.endDate}
                    onChange={(e) => setField('endDate', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch checked={form.isActive} onChange={(e) => setField('isActive', e.target.checked)} />
                    <Typography variant="body2">{tt('Kampanya aktif', 'Campaign active')}</Typography>
                  </Box>
                </Grid>
              </Grid>
              </Box>
            )}

            {(isEditing || wizardStep === 2) && (
              <Box
                id="campaign-section-rules"
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...(isEditing ? { mb: 3, pt: 3, borderTop: 1, borderColor: 'divider' } : {}) }}
              >
                {isEditing && (
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>{tt('Kurallar', 'Rules')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasUsages
                        ? tt('Tutar ve limitler açık. Kullanılmış kupon kodu ve indirim türü kilitli.', 'Value and limits stay open. A used coupon code and discount type stay locked.')
                        : tt('İndirim tutarı, eşik ve limitler. Tür kilitli; henüz kullanım yoksa kod değişebilir.', 'Discount value, thresholds and limits. Type stays locked; the code can change until first use.')}
                    </Typography>
                  </Box>
                )}
                <Alert severity="info">
                  {tt(
                    `Seçtiğin ${selectedTypes.length} tür aşağıda listelenir.`,
                    `${selectedTypes.length} selected types are listed below.`
                  )}
                </Alert>
                {competingCheckoutTypes.length > 1 && (
                  <Alert severity="warning">
                    {tt(
                      'Aynı kayıtta birden fazla sepet indirimi türü varsa kaydı sadeleştir. Kuponu ayrı kampanya olarak bırak; müşteri eklerse ürün veya X al Y öde ile birleşir.',
                      'If this record has more than one checkout discount type, simplify it. Keep the coupon as its own campaign; if the customer adds it, it stacks on the product or Buy X Pay Y saving.'
                    )}
                  </Alert>
                )}

                {showDiscountValue && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2">{tt('Ortak indirim', 'Shared discount')}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      {tt(
                        `${sharedDiscountTypeLabels.join(', ')} aynı değeri kullanır.`,
                        `${sharedDiscountTypeLabels.join(', ')} share this value.`
                      )}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>{tt('İndirim Türü', 'Discount Type')}</InputLabel>
                          <Select
                            label={tt('İndirim Türü', 'Discount Type')}
                            value={form.discountType}
                            disabled={hasUsages}
                            onChange={(e) => setField('discountType', e.target.value)}
                          >
                            <MenuItem value="Percentage">{tt('Yüzde (%)', 'Percentage (%)')}</MenuItem>
                            <MenuItem value="Amount">{tt('Tutar', 'Amount')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label={tt('İndirim Değeri', 'Discount Value')}
                          type="number" fullWidth size="small"
                          value={form.discountValue}
                          onChange={(e) => setField('discountValue', Number(e.target.value) as any)}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">{form.discountType === 'Percentage' ? '%' : tt('TL', 'TRY')}</InputAdornment>,
                          }}
                        />
                      </Grid>
                      {form.discountType === 'Percentage' && (
                        <Grid item xs={12} sm={6} md={4}>
                          <TextField
                            label={tt('Maks. İndirim Tutarı (ops.)', 'Max Discount Amount (opt.)')}
                            type="number" fullWidth size="small"
                            value={form.maxDiscountAmount ?? ''}
                            onChange={(e) => setField('maxDiscountAmount', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                )}

                {showMinCart && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{tt('Min. sepet', 'Min cart')}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          label={tt('Min. Sepet Tutarı', 'Min Cart Amount')}
                          type="number" fullWidth size="small"
                          value={form.minCartAmount ?? ''}
                          onChange={(e) => setField('minCartAmount', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                          helperText={tt(
                            'Kupon, sepet indirimi ve ücretsiz kargo bu eşiği kullanır.',
                            'Coupon, cart discount and free shipping use this threshold.'
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {selectedTypes.map((type) => {
                  const meta = (typeMeta as any)[type];
                  return (
                    <Paper key={type} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'action.hover',
                            color: 'text.secondary',
                          }}
                        >
                          {meta?.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle2">{meta?.label || type}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {type === 'ProductDiscount' && tt('Ürün kartı / detay fiyatına işler. Ortak indirimi kullanır.', 'Applies on product cards and PDP. Uses the shared discount.')}
                            {type === 'Coupon' && tt('Müşteri sepette kod girer. Ortak indirimi kullanır.', 'Customer enters the code in cart. Uses the shared discount.')}
                            {type === 'CartDiscount' && tt('Kod istemez, eşik dolunca sepette otomatik düşer.', 'No code. Applies automatically when the cart hits the threshold.')}
                            {type === 'BuyXPayY' && tt('Kapsamdaki adede göre en ucuz birimleri düşer.', 'Drops the cheapest units in scope by quantity.')}
                            {type === 'FreeShipping' && tt('Eşik dolunca kargo sıfırlanır. Sepet indirimiyle yarışmaz.', 'Shipping becomes free at the threshold. Does not compete with price discounts.')}
                          </Typography>
                        </Box>
                      </Box>

                      {type === 'Coupon' && (
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Alert severity="warning">
                              {tt(
                                'Kupon yalnızca B2C sepette çalışır. Onaylı bayi (B2B) müşteride kupon kutusu gizlenir ve sunucu kuponu uygulamaz.',
                                'Coupons work only for B2C checkout. Approved B2B dealers do not see the coupon box and the server ignores the code.'
                              )}
                            </Alert>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label={tt('Kupon Kodu', 'Coupon Code')}
                              fullWidth
                              size="small"
                              required
                              disabled={hasUsages}
                              value={form.couponCode || ''}
                              onChange={(e) =>
                                setField(
                                  'couponCode',
                                  e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, '')
                                    .slice(0, editingId ? 12 : 6)
                                )
                              }
                              inputProps={{
                                maxLength: editingId ? 12 : 6,
                                style: { letterSpacing: '0.12em', fontWeight: 700 },
                                readOnly: hasUsages,
                              }}
                              helperText={
                                hasUsages
                                  ? tt(
                                      `Bu kod ${usedCount} siparişte kullanıldı; değiştirilemez.`,
                                      `This code was used on ${usedCount} orders and cannot change.`
                                    )
                                  : tt(
                                      'Otomatik 6 haneli kod (harf + rakam). Yenile ikonu ile yeniden üretebilirsiniz.',
                                      'Auto 6-char code (letters + digits). Use refresh to regenerate.'
                                    )
                              }
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {hasUsages ? (
                                      <LockOutlinedIcon fontSize="small" color="action" />
                                    ) : (
                                      <Tooltip title={tt('Yeni kod üret', 'Generate new code')}>
                                        <IconButton
                                          size="small"
                                          edge="end"
                                          aria-label={tt('Yeni kod üret', 'Generate new code')}
                                          onClick={() => setField('couponCode', generateRandomCouponCode(6))}
                                        >
                                          <RefreshIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <TextField
                              label={tt('Toplam Limit', 'Total Limit')}
                              type="number" fullWidth size="small"
                              value={form.usageLimit ?? ''}
                              onChange={(e) => setField('usageLimit', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                            />
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <TextField
                              label={tt('Kişi Başı Limit', 'Per-Customer Limit')}
                              type="number" fullWidth size="small"
                              value={form.usageLimitPerCustomer ?? ''}
                              onChange={(e) => setField('usageLimitPerCustomer', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Switch
                                checked={!!form.allowStackWithOthers}
                                onChange={(e) => setField('allowStackWithOthers', e.target.checked)}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {tt('Diğer indirimlerle birleştir', 'Combine with other discounts')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {form.allowStackWithOthers
                                    ? tt(
                                        'Açık: ürün veya sepet indirimi varsa kupon onun üzerine biner (120 → 96 → ~81,6). Her siparişte çift kesinti olur.',
                                        'On: the coupon stacks on the automatic discount (120 → 96 → ~81.6). Every order takes both cuts.'
                                      )
                                    : tt(
                                        'Kapalı: müşteri kupon yazarsa yalnız kupon uygulanır; ürün/sepet indirimi o siparişte düşer. Kâr için varsayılan budur.',
                                        'Off: if the customer adds a coupon, only the coupon applies and the automatic discount drops on that order. This is the default to protect margin.'
                                      )}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                          {Number(form.usageLimitPerCustomer) > 0 && (
                            <Grid item xs={12}>
                              <Alert severity="info">
                                {tt(
                                  'Aynı e-posta ile ikinci kez kullanılamaz.',
                                  'The same email cannot reuse the coupon.'
                                )}
                              </Alert>
                            </Grid>
                          )}
                        </Grid>
                      )}

                      {type === 'BuyXPayY' && (
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label={tt('Al (X)', 'Buy (X)')}
                              type="number" fullWidth size="small"
                              disabled={hasUsages}
                              value={form.buyQuantity ?? ''}
                              onChange={(e) => setField('buyQuantity', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                              helperText={hasUsages ? tt('Kullanım sonrası kilitli', 'Locked after first use') : undefined}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label={tt('Öde (Y)', 'Pay (Y)')}
                              type="number" fullWidth size="small"
                              disabled={hasUsages}
                              value={form.payQuantity ?? ''}
                              onChange={(e) => setField('payQuantity', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                            />
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              select
                              label={tt('Bedava Birim', 'Free Unit')}
                              fullWidth size="small"
                              value={form.freeUnitSelection === 'Expensive' ? 'Expensive' : 'Cheapest'}
                              onChange={(e) => setField('freeUnitSelection', e.target.value as any)}
                              helperText={
                                form.freeUnitSelection === 'Expensive'
                                  ? tt('En pahalı birim(ler) bedava (mağaza lehine)', 'Most expensive unit(s) free (store favor)')
                                  : tt('En ucuz birim(ler) bedava (müşteri lehine)', 'Cheapest unit(s) free (customer favor)')
                              }
                            >
                              <MenuItem value="Cheapest">{tt('En ucuz bedava', 'Cheapest free')}</MenuItem>
                              <MenuItem value="Expensive">{tt('En pahalı bedava', 'Most expensive free')}</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <TextField
                              label={tt('En Fazla Bedava Adet', 'Max Free Units')}
                              type="number" fullWidth size="small"
                              value={form.maxFreeUnits ?? ''}
                              onChange={(e) => setField('maxFreeUnits', (e.target.value === '' ? null : Number(e.target.value)) as any)}
                              helperText={tt('Boş = sınırsız (sepette toplam)', 'Empty = unlimited (per cart)')}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Switch
                                checked={!!form.buyXPayYSameProductOnly}
                                onChange={(e) => setField('buyXPayYSameProductOnly', e.target.checked)}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {tt('Yalnız aynı üründen say', 'Count same product only')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {form.buyXPayYSameProductOnly
                                    ? tt(
                                        'Açık: bedava adet her ürün kendi içinde gruplanır (ör. aynı üründen 3 al 2 öde).',
                                        'On: free units are grouped per product (e.g. buy 3 pay 2 of the same product).'
                                      )
                                    : tt(
                                        'Kapalı: kapsamdaki (ürün/kategori/marka) farklı ürünler birlikte sayılır.',
                                        'Off: different products within scope (product/category/brand) are counted together.'
                                      )}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      )}

                      {type === 'FreeShipping' && (
                        <Alert severity="warning">
                          {tt(
                            'Ücretsiz kargo hem bu kampanyadan hem Kargo sekmesindeki eşikten gelebilir. Müşteri ikisinden hangisi önce dolarsa ücretsiz görür. Tek eşik istiyorsanız Kargo sekmesindeki eşiği kullanın veya buradaki ücretsiz kargoyu kapatın.',
                            'Free shipping can come from this campaign or from the Shipping tab. Customers get free shipping from whichever threshold they hit first. Use the Shipping tab alone, or turn off free shipping here.'
                          )}
                        </Alert>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}

            {(isEditing || wizardStep === 3) && (
              <Box id="campaign-section-scope" sx={isEditing ? { pt: 3, borderTop: 1, borderColor: 'divider' } : undefined}>
                {isEditing && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{tt('Kapsam', 'Scope')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tt('Nerede geçerli', 'Where it applies')}
                    </Typography>
                  </Box>
                )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {tt('Kampanya hangi ürünlere uygulansın?', 'Which products should this campaign apply to?')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {tt(
                      'Tüm katalog, seçili ürünler, kategoriler veya markalar. Ürün listesi kaydırıldıkça yüklenir; arama ile de bulunabilir.',
                      'All catalog, selected products, categories or brands. Products load as you scroll and can be searched.'
                    )}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                    }}
                  >
                    {([
                      ['All', tt('Tüm Ürünler', 'All Products'), tt('Katalogdaki her ürün', 'Every product in the catalog')],
                      ['Products', tt('Seçili Ürünler', 'Selected Products'), tt('Tek tek ürün seçin', 'Pick products one by one')],
                      ['Categories', tt('Seçili Kategoriler', 'Selected Categories'), tt('Kategoriye göre uygula', 'Apply by category')],
                      ['Brands', tt('Seçili Markalar', 'Selected Brands'), tt('Markaya göre uygula', 'Apply by brand')],
                    ] as const).map(([value, label, hint]) => {
                      const selected = form.scopeType === value;
                      return (
                        <Card
                          key={value}
                          variant="outlined"
                          sx={{
                            borderColor: selected ? 'primary.main' : 'divider',
                            borderWidth: selected ? 2 : 1,
                            bgcolor: selected ? 'action.selected' : 'background.paper',
                          }}
                        >
                          <CardActionArea
                            onClick={() => setField('scopeType', value)}
                            sx={{ p: 1.5, height: '100%' }}
                          >
                            <Typography variant="body2" fontWeight={700}>{label}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {hint}
                            </Typography>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                  </Box>
                </Grid>
                {form.scopeType === 'Products' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={products}
                      loading={productLoading}
                      filterSelectedOptions
                      getOptionLabel={productLabel}
                      value={products.filter((p) => (form.productIds || []).includes(p.id))}
                      onChange={(_, val) => {
                        const ids = val.map((v) => v.id);
                        selectedProductIdsRef.current = ids;
                        setField('productIds', ids);
                      }}
                      onInputChange={(_, value, reason) => {
                        if (reason !== 'input') return;
                        setProductQuery(value);
                        if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
                        productSearchTimer.current = setTimeout(() => {
                          productHasMoreRef.current = true;
                          loadProductPage(1, value, true);
                        }, 300);
                      }}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      ListboxProps={{
                        style: { maxHeight: 280 },
                        onScroll: (event) => {
                          const list = event.currentTarget;
                          if (list.scrollTop + list.clientHeight >= list.scrollHeight - 48) {
                            loadProductPage(productPageRef.current + 1, productQuery, false);
                          }
                        },
                      }}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap>{option.name}</Typography>
                            {option.stockCode && (
                              <Typography variant="caption" color="text.secondary">
                                {option.stockCode}
                              </Typography>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tt('Ürünler', 'Products')}
                          placeholder={tt('Ürün adı veya stok kodu ara...', 'Search by name or stock code...')}
                          helperText={tt(
                            'Listeyi kaydırarak devamı gelir. Seçilen ürünler üstte chip olarak kalır.',
                            'Scroll the list to load more. Selected products stay as chips.'
                          )}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {productLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      noOptionsText={
                        productLoading
                          ? tt('Ürünler yükleniyor...', 'Loading products...')
                          : tt('Ürün bulunamadı', 'No products found')
                      }
                    />
                  </Grid>
                )}
                {form.scopeType === 'Categories' && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      {tt(
                        'Kategori kapsamı yalnızca seçilen kategorinin kendisine işler. Alt kategorilerdeki ürünler dahil edilmez.',
                        'Category scope matches only the selected category IDs. Products in child categories are not included.'
                      )}
                    </Alert>
                    {catalogError && (
                      <Alert
                        severity="error"
                        sx={{ mb: 1.5 }}
                        action={
                          <Button color="inherit" size="small" onClick={() => void ensureCatalog([], true)}>
                            {tt('Yeniden dene', 'Retry')}
                          </Button>
                        }
                      >
                        {catalogError}
                      </Alert>
                    )}
                    <Autocomplete
                      multiple
                      size="small"
                      options={categories}
                      loading={catalogLoading}
                      getOptionLabel={(o) => o.name}
                      value={[
                        ...categories.filter((c) => (form.categoryIds || []).includes(c.id)),
                        ...(form.categoryIds || [])
                          .filter((id) => !categories.some((c) => c.id === id))
                          .map((id) => ({ id, name: id })),
                      ]}
                      onChange={(_, val) => setField('categoryIds', val.map((v) => v.id))}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      noOptionsText={
                        catalogLoading
                          ? tt('Kategoriler yükleniyor...', 'Loading categories...')
                          : tt('Kategori bulunamadı', 'No categories found')
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tt('Kategoriler', 'Categories')}
                          placeholder={tt('Kategori ara ve seç...', 'Search and select categories...')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {catalogLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
                {form.scopeType === 'Brands' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={brands}
                      getOptionLabel={(o) => o.name}
                      value={brands.filter((b) => (form.brandIds || []).includes(b.id))}
                      onChange={(_, val) => setField('brandIds', val.map((v) => v.id))}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={tt('Markalar', 'Brands')}
                          placeholder={tt('Marka ara ve seç...', 'Search and select brands...')}
                        />
                      )}
                    />
                  </Grid>
                )}
                {form.scopeType === 'All' && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      {tt(
                        'Kampanya e-ticaret kataloğundaki tüm ürünlere uygulanır.',
                        'The campaign will apply to every product in the store catalog.'
                      )}
                    </Alert>
                  </Grid>
                )}

                {!isEditing && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{tt('Özet', 'Summary')}</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '160px 1fr' },
                      rowGap: 1,
                      columnGap: 2,
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {[
                      [tt('Ad', 'Name'), form.name || '—'],
                      [tt('Tür', 'Type'), selectedTypes.map((t) => typeLabel(t)).join(' + ') || '—'],
                      [tt('Tarih', 'Dates'), `${form.startDate ? formatDate(form.startDate) : '—'} → ${form.endDate ? formatDate(form.endDate) : '—'}`],
                      [tt('Özel Gün', 'Special Day'), getSpecialDayLabel(form.specialDay, isTr)],
                      [tt('Kurallar', 'Rules'), rulesSummary()],
                      [tt('Kapsam', 'Scope'), scopeLabel()],
                      [tt('Durum', 'Status'), form.isActive ? tt('Aktif', 'Active') : tt('Pasif', 'Passive')],
                      [
                        tt('Vitrin', 'Storefront'),
                        editingId ? `/campaigns/${editingId}` : tt('Kayıt sonrası /campaigns/:id', 'After save: /campaigns/:id'),
                      ],
                    ].map(([k, v]) => (
                      <React.Fragment key={String(k)}>
                        <Typography variant="caption" color="text.secondary">{k}</Typography>
                        <Typography variant="body2" fontWeight={600}>{v}</Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                </Grid>
                )}
              </Grid>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2.5 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              position: isEditing ? 'sticky' : 'static',
              bottom: 0,
              py: isEditing ? 1.5 : 0,
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <Button onClick={closeEditor} disabled={saving}>{tt('İptal', 'Cancel')}</Button>
            {isEditing ? (
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Değişiklikleri Kaydet', 'Save Changes')}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<ChevronLeftIcon />}
                  onClick={goBack}
                  disabled={wizardStep === 0 || saving}
                >
                  {tt('Geri', 'Back')}
                </Button>
                {wizardStep < WIZARD_STEPS.length - 1 ? (
                  <Button variant="contained" endIcon={<ChevronRightIcon />} onClick={goNext}>
                    {tt('İleri', 'Next')}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? tt('Kaydediliyor...', 'Saving...') : tt('Kaydet', 'Save')}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
      <>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">{tt('Kampanyalar', 'Campaigns')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {tt('E-ticaret sitenizdeki indirim ve kampanyaları yönetin', 'Manage discounts and campaigns on your store')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GuideButton guideKey="ecommerce.campaigns" />
          <Tooltip title={tt('Yenile', 'Refresh')}>
            <IconButton onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<ScienceOutlinedIcon />} onClick={openSimulator}>
            {tt('Sepeti dene', 'Try cart')}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {tt('Yeni Kampanya', 'New Campaign')}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(_, v) => { setStatusFilter(v); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="all" label={`${tt('Tümü', 'All')} (${statusCounts.all})`} />
          <Tab value="active" label={`${tt('Aktif', 'Active')} (${statusCounts.active || 0})`} />
          <Tab value="upcoming" label={`${tt('Yaklaşan', 'Upcoming')} (${statusCounts.upcoming || 0})`} />
          <Tab value="expired" label={`${tt('Süresi Doldu', 'Expired')} (${statusCounts.expired || 0})`} />
          <Tab value="passive" label={`${tt('Pasif', 'Passive')} (${statusCounts.passive || 0})`} />
        </Tabs>
      </Paper>

      <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          sx={{ flex: 1, minWidth: 220 }}
          placeholder={tt('Kampanya adı veya kupon kodu ara...', 'Search by name or coupon code...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>{tt('Özel Gün / Sezon', 'Special Day / Season')}</InputLabel>
          <Select
            label={tt('Özel Gün / Sezon', 'Special Day / Season')}
            value={specialDayFilter}
            onChange={(e) => { setSpecialDayFilter(String(e.target.value)); setPage(0); }}
          >
            <MenuItem value="all">{tt('Tüm özel günler', 'All special days')}</MenuItem>
            <MenuItem value="none">{tt('Özel gün yok', 'No special day')}</MenuItem>
            {CAMPAIGN_SPECIAL_DAYS.filter((d) => d.key).map((day) => (
              <MenuItem key={day.key} value={day.key}>
                {isTr ? day.labelTr : day.labelEn}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <AnimatePresence mode="wait">
          <motion.div
            key={`${statusFilter}-${specialDayFilter}-${searchTerm}-${page}`}
            variants={listVariants}
            initial="hidden"
            animate="show"
            exit={{ opacity: 0, y: -4, transition: { duration: 0.12 } }}
          >
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{tt('Ad', 'Name')}</TableCell>
                      <TableCell>{tt('Tür', 'Type')}</TableCell>
                      <TableCell>{tt('Özel Gün', 'Special Day')}</TableCell>
                      <TableCell>{tt('İndirim', 'Discount')}</TableCell>
                      <TableCell>{tt('Tarih Aralığı', 'Date Range')}</TableCell>
                      <TableCell align="center">{tt('Kullanım', 'Usage')}</TableCell>
                      <TableCell align="center">{tt('Durum', 'Status')}</TableCell>
                      <TableCell align="center">{tt('Aktif', 'Active')}</TableCell>
                      <TableCell align="right">{tt('İşlemler', 'Actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                          {tt(
                            'Kampanya bulunamadı. “Yeni Kampanya” ile özel gün/sezon seçerek oluşturabilirsiniz.',
                            'No campaigns found. Create one with “New Campaign” and pick a special day/season.'
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paged.map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          component={motion.tr as any}
                          variants={rowVariants}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                            {c.couponCode && (
                              <Chip size="small" label={c.couponCode} variant="outlined" sx={{ mt: 0.5, mr: 0.5 }} />
                            )}
                            {normalizeTypes(c).includes('Coupon') && (
                              <Chip
                                size="small"
                                variant="outlined"
                                color={c.allowStackWithOthers ? 'warning' : 'default'}
                                label={c.allowStackWithOthers
                                  ? tt('Birleşir', 'Stacks')
                                  : tt('Tek başına', 'Alone')}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ lineHeight: 1.35 }}>
                              {typesLabel(c)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {c.specialDay ? (
                              <Chip size="small" color="secondary" variant="outlined" label={getSpecialDayLabel(c.specialDay, isTr)} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{discountLabel(c)}</TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">{formatDate(c.startDate)}</Typography>
                            <Typography variant="caption" color="text.secondary">→ {formatDate(c.endDate)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={tt('Kullananları gör', 'See who used this')}>
                              <Button
                                size="small"
                                color="inherit"
                                startIcon={<PeopleOutlineIcon fontSize="small" />}
                                onClick={() => openUsages(c)}
                                sx={{ minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                              >
                                {c.usedCount || 0}{c.usageLimit ? ` / ${c.usageLimit}` : ''}
                              </Button>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={statusLabel(c.status)} color={STATUS_COLORS[c.status] || 'default'} />
                          </TableCell>
                          <TableCell align="center">
                            <Switch size="small" checked={!!c.isActive} onChange={() => handleToggle(c)} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title={tt('Tekrarla', 'Repeat')}>
                              <IconButton size="small" onClick={() => openRepeat(c)}><ReplayIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title={tt('Düzenle', 'Edit')}>
                              <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title={tt('Sil', 'Delete')}>
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(c)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Paper>
          </motion.div>
        </AnimatePresence>
      )}
      </>
      )}

      <Dialog
        open={!!usageTarget}
        onClose={() => setUsageTarget(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {tt('Kampanya kullanımları', 'Campaign usages')}
          {usageTarget?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {usageTarget.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {usageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : usageError ? (
            <Alert severity="error" sx={{ my: 1 }}>{usageError}</Alert>
          ) : usageRows.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {tt('Bu kampanyayı henüz kimse kullanmamış.', 'No one has used this campaign yet.')}
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tt('Sipariş', 'Order')}</TableCell>
                  <TableCell>{tt('Müşteri', 'Customer')}</TableCell>
                  <TableCell>{tt('Kupon', 'Coupon')}</TableCell>
                  <TableCell align="right">{tt('İndirim', 'Discount')}</TableCell>
                  <TableCell>{tt('Tarih', 'Date')}</TableCell>
                  <TableCell align="center">{tt('Durum', 'Status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usageRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.orderNumber || row.orderId || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.customer}</Typography>
                      {row.isGuest && row.customer !== 'Misafir' && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {tt('Misafir', 'Guest')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.couponCode ? (
                        <Chip size="small" variant="outlined" label={row.couponCode} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{formatMoney(row.discountAmount)}</TableCell>
                    <TableCell>
                      <Typography variant="caption">{formatDateTime(row.usedAt)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={row.reversed ? tt('İade / iptal', 'Refunded / cancelled') : tt('Kullanıldı', 'Used')}
                        color={row.reversed ? 'default' : 'success'}
                        variant={row.reversed ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 2 }}>
          <TablePagination
            component="div"
            count={usageTotal}
            page={usagePage}
            onPageChange={(_, p) => usageTarget && loadUsages(usageTarget, p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            onRowsPerPageChange={() => undefined}
          />
          <Button onClick={() => setUsageTarget(null)}>{tt('Kapat', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={simOpen}
        onClose={() => setSimOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {tt('Sepeti dene', 'Try cart')}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {tt(
              'Sipariş veya kullanım yazılmaz. Kayıtlı kampanyaların bu sepette hangisinin kazanacağını görürsünüz.',
              'Nothing is saved. See which saved campaign would win on this cart.'
            )}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={products}
            getOptionLabel={(o) => o.stockCode ? `${o.name} (${o.stockCode})` : o.name}
            loading={productLoading}
            onInputChange={(_, value) => {
              if (productSearchTimer.current) clearTimeout(productSearchTimer.current);
              productSearchTimer.current = setTimeout(() => {
                void loadProductPage(1, value, true);
              }, 300);
            }}
            onChange={(_, val) => {
              if (!val?.id) return;
              setSimLines((prev) => (
                prev.some((l) => l.id === val.id)
                  ? prev
                  : [...prev, { id: val.id, name: val.name, quantity: 1 }]
              ));
              setSimResult(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={tt('Ürün ekle', 'Add product')}
                placeholder={tt('Ürün adı veya stok kodu...', 'Name or SKU...')}
              />
            )}
          />
          {simLines.length > 0 && (
            <Table size="small" sx={{ mt: 2 }}>
              <TableBody>
                {simLines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.name}</TableCell>
                    <TableCell width={96}>
                      <TextField
                        size="small"
                        type="number"
                        value={line.quantity}
                        inputProps={{ min: 1, max: 99 }}
                        onChange={(e) => {
                          const qty = Math.max(1, Math.min(99, Number(e.target.value) || 1));
                          setSimLines((prev) => prev.map((l) => (l.id === line.id ? { ...l, quantity: qty } : l)));
                          setSimResult(null);
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" width={72}>
                      <Button size="small" color="inherit" onClick={() => {
                        setSimLines((prev) => prev.filter((l) => l.id !== line.id));
                        setSimResult(null);
                      }}>
                        {tt('Çıkar', 'Remove')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <TextField
            size="small"
            fullWidth
            sx={{ mt: 2 }}
            label={tt('Kupon kodu (opsiyonel)', 'Coupon code (optional)')}
            value={simCoupon}
            onChange={(e) => { setSimCoupon(e.target.value.toUpperCase()); setSimResult(null); }}
          />
          {simError && <Alert severity="error" sx={{ mt: 2 }}>{simError}</Alert>}
          {simResult && (
            <Box sx={{ mt: 2 }}>
              {simResult.exclusiveHint && (
                <Alert severity="info" sx={{ mb: 2 }}>{simResult.exclusiveHint}</Alert>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{tt('Sonuç', 'Result')}</Typography>
              <Typography variant="body2">
                {tt('Ara toplam', 'Subtotal')}: {formatMoney(simResult.merchandiseSubtotal)}
              </Typography>
              {simResult.applied.filter((a) => a.discountAmount > 0 || a.type === 'FreeShipping').map((a) => (
                <Typography key={`${a.campaignId}:${a.type}`} variant="body2">
                  {a.campaignName || typeLabel(a.type)}
                  {a.discountAmount > 0 ? ` − ${formatMoney(a.discountAmount)}` : ''}
                  {` (${typeLabel(a.type)})`}
                </Typography>
              ))}
              {simResult.applied.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {tt('Bu sepette kampanya uygulanmaz.', 'No campaign applies to this cart.')}
                </Typography>
              )}
              {simResult.freeShipping && (
                <Typography variant="body2">{tt('Ücretsiz kargo', 'Free shipping')}</Typography>
              )}
              <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
                {tt('Ödenecek', 'Payable')}: {formatMoney(simResult.payable)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimOpen(false)}>{tt('Kapat', 'Close')}</Button>
          <Button variant="contained" onClick={runSimulator} disabled={simLoading}>
            {simLoading ? tt('Hesaplanıyor...', 'Calculating...') : tt('Hesapla', 'Calculate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme onayı */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{tt('Kampanyayı Sil', 'Delete Campaign')}</DialogTitle>
        <DialogContent>
          <Typography>
            {tt('Bu kampanyayı silmek istediğinize emin misiniz?', 'Are you sure you want to delete this campaign?')}
          </Typography>
          {deleteTarget && <Typography fontWeight={600} sx={{ mt: 1 }}>{deleteTarget.name}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{tt('İptal', 'Cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>{tt('Sil', 'Delete')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignManagement;
